import React from "react";
import FormField from "../../../common/FormField/FormField";
import { Formik } from "formik";
import { CustomFileUpload, CustomInputField } from "../../../common/FormInputs";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { navigate } from "@reach/router";
import { Popper } from "@material-ui/core";
import { renameImageValidationSchema } from "../../../../helpers/validationSchema";
import { StyledForm, ButtonGroup, BackButton, SubmitButton } from "./styles";
import AWSService from "../../../../services/AWSService";
import { getFileName, getErrorMessage } from "../../../../helpers/functions";
import {
  Card,
  FormGrid,
  Label,
  PageWrapper,
  Section,
  StyledAutocomplete,
} from "./SplitAndUpload";
import useRenameJobs from "../../../../hooks/useRenameJobs";

const CustomPopper = (props) => {
  const anchorEl = props.anchorEl;
  const width = anchorEl ? anchorEl.clientWidth : undefined;

  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width: width || undefined,
        zIndex: 1300,
      }}
      placement="bottom-start"
      modifiers={{
        flip: { enabled: true },
        preventOverflow: { enabled: true, boundariesElement: "scrollParent" },
      }}
    />
  );
};

const ActiveJobs = ({ jobs, onDownload, onRemove, onClearCompleted }) => {
  if (!jobs?.length) return null;

  return (
    <div style={{ marginTop: "30px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <h3 style={{ margin: 0, color: "#293461" }}>Active Rename Jobs</h3>
        {jobs.some(
          (j) => j.status === "completed" || j.status === "failed",
        ) && (
          <button
            onClick={onClearCompleted}
            type="button"
            style={{
              padding: "8px 16px",
              backgroundColor: "#f44336",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "0.9rem",
            }}
          >
            Clear Completed
          </button>
        )}
      </div>

      {jobs.map((job) => (
        <div
          key={job.job_id}
          style={{
            padding: "16px",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            marginBottom: "12px",
            backgroundColor: "#fff",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div style={{ flex: 1 }}>
              <strong>{job.pre_text || "Rename Job"}</strong>
              <div style={{ fontSize: "0.9rem", marginTop: "6px" }}>
                Status:{" "}
                <span
                  style={{
                    fontWeight: "600",
                    color:
                      job.status === "completed"
                        ? "green"
                        : job.status === "failed"
                          ? "red"
                          : "#f57c00",
                  }}
                >
                  {job.status?.toUpperCase() || "PROCESSING"}
                </span>
              </div>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              {job.status === "completed" && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDownload(job);
                  }}
                  type="button"
                  style={{
                    padding: "10px 18px",
                    backgroundColor: "#0f766e",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                >
                  Download
                </button>
              )}
              {(job.status === "completed" || job.status === "failed") && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemove(job.job_id);
                  }}
                  type="button"
                  style={{
                    padding: "10px 14px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const RenameImage = ({ menuItems }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  // Global Hook Use பண்ணுங்கள்
  const { activeJobs, addJob, removeJob, clearCompleted, fetchJobStatus } =
    useRenameJobs();

  const s3Upload = async (file, field, setFieldValue, setFieldError) => {
    if (!file) return;
    try {
      setLoader({ state: true, message: `Uploading ${file.name}...` });
      const payload = { key: file.name };
      const s3Response = await AWSService.getS3URL(payload);
      if (s3Response.url) {
        const res = await AWSService.uploadToS3(s3Response.url, file);
        setFieldValue(field, res);
      }
    } catch (error) {
      setFieldError(field, getErrorMessage(error));
    } finally {
      setLoader({ state: false });
    }
  };

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: "Starting background rename job..." });

      const formData = new FormData();
      ["excelFile", "zipFilePath", "preInputText", "postInputText"].forEach(
        (key) => {
          if (values[key]) formData.append(key, values[key]);
        },
      );

      const response = await fetch(
        "https://staging-api.justact.co.in/rename-pdf",
        {
          method: "POST",
          body: formData,
        },
      );

      if (response.ok) {
        const data = await response.json();
        if (data.job_id) {
          const newJob = {
            job_id: data.job_id,
            status: "processing",
            pre_text: values.preInputText || "",
            post_text: values.postInputText || "",
            createdAt: new Date().toISOString(),
            ...data,
          };

          addJob(newJob);
          enqueueSnackbar("Rename job started in background!", {
            variant: "success",
          });
          resetForm();
        }
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  const handleDownload = async (job) => {
    enqueueSnackbar("Fetching latest status...", { variant: "info" });
    await fetchJobStatus(job.job_id); // Global fetch

    const updatedJob = activeJobs.find((j) => j.job_id === job.job_id);
    const url = updatedJob?.url || job.url;

    if (!url) {
      enqueueSnackbar("Download link not ready yet", { variant: "warning" });
      return;
    }

    const a = document.createElement("a");
    a.href = url;
    a.download = getFileName(url) || "renamed_files.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleRemove = (jobId) => removeJob(jobId);
  const handleClearCompleted = () => clearCompleted();

  const inputs = [{ name: "postInputText", type: "text", label: "Post Name" }];

  const upload_templates = [
    { name: "excelFile", label: "Upload Xlsx", accept: ".xlsx" },
    { name: "zipFilePath", label: "Zip File (zip)", accept: ".zip" },
  ];

  return (
    <PageWrapper>
      <Card>
        <Formik
          initialValues={{
            preInputText: "",
            postInputText: "",
            excelFile: "",
            zipFilePath: "",
          }}
          validationSchema={renameImageValidationSchema}
          enableReinitialize
          onSubmit={onFormSubmit}
        >
          {({
            values,
            handleChange,
            errors,
            touched,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setFieldError,
          }) => (
            <StyledForm>
              {/* உங்கள் form fields */}
              <Section>
                <FormGrid>
                  <div>
                    <Label>Pre Name</Label>
                    <StyledAutocomplete
                      id="preInputText-autocomplete"
                      size="small"
                      options={menuItems}
                      groupBy={(option) => option.category}
                      getOptionLabel={(option) => option.name}
                      PopperComponent={CustomPopper}
                      renderInput={(params) => (
                        <div ref={params.InputProps.ref}>
                          <CustomInputField
                            {...params}
                            variant="outlined"
                            onBlur={handleBlur}
                            error={
                              errors["preInputText"] && touched["preInputText"]
                            }
                            helperText={
                              touched["preInputText"] && errors["preInputText"]
                            }
                            placeholder="Select pre name..."
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                            }}
                          />
                        </div>
                      )}
                      renderGroup={(params) => (
                        <div style={{ padding: "8px 0" }}>
                          <div
                            style={{
                              padding: "10px 16px",
                              fontWeight: "700",
                              fontSize: "11px",
                              color: "#0f766e",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              backgroundColor: "#f0fdf4",
                              borderRadius: "6px",
                              margin: "4px 8px",
                            }}
                          >
                            {params.group}
                          </div>
                          <div
                            style={{
                              padding: "4px 0",
                              color: "#293461",
                              fontFamily: "openSans-SemiBold, sans-serif",
                            }}
                          >
                            {params.children}
                          </div>
                        </div>
                      )}
                      noOptionsText={
                        <div
                          style={{
                            color: "#949ab0",
                            fontFamily: "openSans-SemiBold, sans-serif",
                            fontSize: "14px",
                            padding: "8px",
                          }}
                        >
                          No document type found
                        </div>
                      }
                      onChange={(event, newValue) =>
                        setFieldValue("preInputText", newValue?.name || "")
                      }
                    />
                  </div>

                  <div>
                    {inputs.map((input, index) => (
                      <FormField
                        key={index}
                        input={input}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    ))}
                  </div>
                </FormGrid>
              </Section>

              <Section>
                <FormGrid>
                  {upload_templates.map((upload) => (
                    <div key={upload.name}>
                      <Label>
                        {upload.label} <span className="required-star">*</span>
                      </Label>
                      <CustomFileUpload
                        error={touched[upload.name] && errors[upload.name]}
                        setFile={(file) =>
                          s3Upload(
                            file,
                            upload.name,
                            setFieldValue,
                            setFieldError,
                          )
                        }
                        style={{ width: "100%", boxSizing: "border-box" }}
                        accept={upload.accept}
                        file={{ name: getFileName(values[upload.name]) }}
                      />
                    </div>
                  ))}
                </FormGrid>
              </Section>

              <ButtonGroup>
                <BackButton onClick={() => navigate("/dashboard/case-manager")}>
                  Back
                </BackButton>
                <SubmitButton onClick={handleSubmit}>
                  Start Rename Job
                </SubmitButton>
              </ButtonGroup>

              <ActiveJobs
                jobs={activeJobs}
                onDownload={handleDownload}
                onRemove={handleRemove}
                onClearCompleted={handleClearCompleted}
              />
            </StyledForm>
          )}
        </Formik>
      </Card>
    </PageWrapper>
  );
};

export default RenameImage;
